import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { handleSessionExpiration } from "../store";
import { useEffect } from "react";
import showToast from "../utils/toastNotifications";

/**
 * Custom hook to monitor session token and handle expiration
 * Uses Redux to manage session state and show/hide the sign-in modal
 */

const useSessionMonitor = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  useEffect(() => {
    // Skip session monitoring for pending users
    if (user?.status === "pending") {
      console.log("Skipping session monitor for pending user");
      return;
    }

    // Check if token is expired
    const checkTokenExpiration = () => {
      // Only check if user is authenticated AND not pending
      if (!isAuthenticated || user?.status === "pending") return;

      const token = localStorage.getItem("token");

      // If no token exists, handle expiration immediately
      if (!token) {
        handleTokenExpiration();
        return;
      }

      try {
        // Split the token and get the payload part
        const [, payloadBase64] = token.split(".");

        // Decode the base64 payload
        const decodedPayload = atob(payloadBase64);

        // Parse the JSON payload
        const tokenData = JSON.parse(decodedPayload);

        // Check expiration
        const expirationTime = tokenData.exp * 1000; // Convert to milliseconds

        if (Date.now() >= expirationTime) {
          handleTokenExpiration();
        }
      } catch (error) {
        console.error("Error checking token expiration:", error);
        // Handle invalid token by logging out
        handleTokenExpiration();
      }
    };

    const handleTokenExpiration = () => {
      // Show toast notification immediately
      showToast("error", "Session Expired. Please Sign in again");
      
      // Dispatch session expiration which will open modal and handle cleanup
      dispatch(handleSessionExpiration());
    };

    const handleStorageChange = (e) => {
      // Check if token is removed and user is authenticated
      if (e.key === "token" && !e.newValue && isAuthenticated) {
        handleTokenExpiration();
      }
    };

    // Initial check with a slight delay to allow auth process to complete
    const initialCheckTimeout = setTimeout(() => {
      checkTokenExpiration();
    }, 1000);

    // Set up periodic checks
    const intervalId = setInterval(checkTokenExpiration, 60000); // Check every minute

    // Set up storage event listener
    window.addEventListener("storage", handleStorageChange);

    // Cleanup
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(intervalId);
      clearTimeout(initialCheckTimeout);
    };
  }, [dispatch, isAuthenticated, user?.status, location]);
};

export default useSessionMonitor;